// src/hooks/demoReset.ts
// ============================================================
// GLOBAL PRESENTER RESET — returns ALL mutable demo state to the Act-1 baseline in one call.
//
// Wired into the useDemoPlan keydown (Esc×3 / Ctrl+Shift+D) and the mounted DemoModeIndicator
// pill. Uses LITERAL key/event names (documented against their owning hooks) so this module
// pulls in no other module — no import cycle, no shared-hook → prototype dependency.
//
// Baseline restored:
//   dva_demo_plan_override        → cleared  (→ real/freemium; undoes the Enterprise Pro force)
//   dva_demo_enterprise_mode      → cleared  (→ 'standard')
//   dva_demo_prior_plan           → cleared  (the Enterprise prior-tier stash)
//   dva_demo_enterprise_run       → cleared  (→ default = latest run, hero 82)
//   dva_demo_enterprise_scenario  → cleared  (adds the previously-missing scenario clear path)
//   dva_demo_uploaded_documents   → cleared  (→ no uploaded docs, so the coverage panel
//                                              reopens at 26 of 38 informed / 0 evidence-backed)
//   dva_demo_initiatives          → cleared  (→ no imported initiatives; Initiatives back to 0%)
//   ea_finalist_ids/_order        → cleared  (→ empty Add-to-Portfolio selection; EAContext
//   ea_flagged_assumptions                     re-reads live via the 'ea_demo_reset' event)
//
// Coupling handled: entering Enterprise forces plan_override='pro' and stashes the prior tier;
// clearing mode + plan + prior together undoes all of it atomically.
// ============================================================

// The key list now lives in src/lib/demoSeedVersion, because the seed-version purge needs the
// exact same set. One list, two consumers — a key can no longer be added to the presenter
// reset and forgotten in the version purge (or the reverse). Importing it adds no cycle:
// demoSeedVersion imports nothing.
//
// NOTE: this list grew when it moved. It previously omitted `dva_meeting_mode`,
// `ea_category_weights` and `ea_measure_weights_v2`, so a presenter who changed weights
// mid-demo kept those changes through a "full" reset. They are cleared now.
export { DEMO_STATE_KEYS } from '../lib/demoSeedVersion';
import { DEMO_STATE_KEYS } from '../lib/demoSeedVersion';

/** Atomically reset every mutable demo-state key and notify all synced hooks to re-read. */
export function resetDemo(): void {
  if (typeof window === 'undefined') return;

  DEMO_STATE_KEYS.forEach((k) => localStorage.removeItem(k));

  // Notify synced hooks. Dispatching with NO detail makes each handler fall back to its own
  // seed default (detail ?? read()) now that the key is gone; the plan handler wants null.
  window.dispatchEvent(new CustomEvent('dva_demo_plan_change', { detail: null })); // → freemium
  window.dispatchEvent(new CustomEvent('dva_demo_enterprise_mode_change')); // → 'standard'
  window.dispatchEvent(new CustomEvent('dva_demo_enterprise_run_change')); // → default (latest)
  window.dispatchEvent(new CustomEvent('ea_demo_reset')); // EAContext clears live finalists/portfolio
  // useDemoUploads listens to BOTH its own event and ea_demo_reset, so the document list and
  // the imported initiatives table empty at the same moment the trackers fall back to seed.
  window.dispatchEvent(new CustomEvent('dva_demo_uploads_change'));
  // RE-ARM the Evaluate interstitial: after a reset the presenter walks the personalization
  // beats again, so the next entry into Evaluate should replay the run. (The key was just
  // cleared above; setting it back is what "re-armed" means here.)
  localStorage.setItem('dva_demo_eval_run_pending', '1');
  window.dispatchEvent(new CustomEvent('dva_demo_eval_run_change', { detail: true }));
  // dva_demo_enterprise_scenario has no live listener — removing the key is sufficient
  // (loadSavedScenario reads fresh on next access).
}

export default resetDemo;
